import currencyFormatter from 'currency-formatter';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import omit from 'lodash/omit';
import { API_MYEDUZZ } from 'settings';

import type IProfile from 'v2/interfaces/profile';

import { agent } from './agent';

export const addScrollPage = () => {
  document.body.classList.remove('no-scroll');
  document.body.style.paddingRight = '0';
};

export const removeScrollPage = () => {
  document.body.classList.add('no-scroll');
  document.body.style.paddingRight = '15px';
};

export const clearMoneyMask = (value: string | number = 0) => {
  value = value.toString();
  if (value.match(/^\d+$/)) {
    value = `${value}.00`;
  }
  if (value.match(/^\d+\.\d+$/)) {
    value = Number(value).toFixed(2);
  }
  value = value.replace(/\D+/gi, '');
  const intval = value.substr(0, value.length - 2);
  const cents = value.substr(value.length - 2, 2);
  value = `${intval}.${cents}`;

  return value;
};

export const addMoneyMask = (value: string | number) => {
  if (!value) return value;
  value = value.toString();
  if (value.match(/^\d+$/)) {
    value = `${value}.00`;
  }
  if (value.match(/^\d+\.\d+$/)) {
    value = Number(value).toFixed(2);
  }
  value = value.replace(/\D+/gi, '');
  const intval = value.substr(0, value.length - 2);
  const cents = value.substr(value.length - 2, 2);
  value = `${intval}.${cents}`;
  return currencyFormatter.format(Number(value), { code: 'BRL', precision: 2, symbol: '' });
};

export const emailIsValid = (value: string) => {
  if (value) {
    // eslint-disable-next-line max-len
    let rgx =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return rgx.test(String(value).toLowerCase()); // true or false
  } else {
    return false;
  }
};

export const clearLocalStorage = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('id');
  localStorage.removeItem('name');
  localStorage.removeItem('access_legacy');
  localStorage.removeItem('user_type');
  localStorage.removeItem('photo');
};

export const scrollToElement = (element: any, duration?: number) => {
  let startingY = window.pageYOffset;
  let diff = element ? element.getBoundingClientRect().top - startingY : 0;
  let start = 0;

  if (!duration) {
    duration = 2000;
  }

  window.requestAnimationFrame(function step(timestamp) {
    if (!start) start = timestamp;

    let time = timestamp - start;
    let percent = Math.min(time / duration, 1);

    window.scrollTo(0, startingY + diff * percent);

    if (time < duration) {
      window.requestAnimationFrame(step);
    }
  });
};

export const getQueryString = (expectedFields: any, filters: any) => {
  let queryParams: string = '';
  if (filters) {
    for (let param in expectedFields) {
      const p = expectedFields[param];
      const fil = filters[param];
      if (fil) {
        if (p.isMultiple) {
          if (Array.isArray(fil)) {
            for (let a = 0; a < fil.length; a++) {
              queryParams += `&${p.apiName}[]=${fil[a].value}`;
            }
          }
        } else {
          if (fil.value || fil.value === false) {
            queryParams += `&${p.apiName}=${fil.value}`;
          } else {
            queryParams += `&${p.apiName}=${fil}`;
          }
        }
      }
    }
  }
  if (queryParams) {
    queryParams = queryParams.substr(1);
  }
  return queryParams || '';
};

export const hjTagging = (tags: string[]) => {
  if (typeof hj !== 'undefined') {
    hj('tagRecording', tags);
  }
};

export const userHasNoDocument = async (profile: IProfile) => {
  const isForeign = profile.type === 'E';
  const hasDocument = profile.document_number;
  const hasValidAddress = profile.address_street && profile.address_zip_code;
  const hasAcceptedTerms = profile.fiscal_responsibility === 1;

  if (isForeign || (hasDocument && hasValidAddress && hasAcceptedTerms)) {
    return true;
  }

  return false;
};

export const userHasNoDocumentAlert = async (): Promise<{ profile: IProfile; hasDocument: boolean }> => {
  const result = await agent.get(`${API_MYEDUZZ}/profile`);
  const profile: IProfile = result.data.data;

  try {
    const isForeign = profile.type === 'E';
    const hasDocument = profile.document_number;
    const hasValidAddress =
      profile.address_street &&
      profile.address_zip_code &&
      profile.address_number &&
      profile.address_neighborhood &&
      profile.address_city_id &&
      profile.address_state &&
      profile.address_country;
    const hasAcceptedTerms = profile.fiscal_responsibility === 1;

    if (isForeign || (hasDocument && hasValidAddress && hasAcceptedTerms)) {
      return {
        profile: { ...profile },
        hasDocument: true,
      };
    }
    return {
      profile: { ...profile },
      hasDocument: false,
    };
  } catch (error) {
    return {
      profile: { ...profile },
      hasDocument: false,
    };
  }
};

export const functions = {
  addScrollPage,
  removeScrollPage,
  clearMoneyMask,
  addMoneyMask,
  emailIsValid,
  clearLocalStorage,
  scrollToElement,
  getQueryString,
  hjTagging,
  isFunction,
  cloneDeep,
  isEqual,
  omit,
};
